import { useCallback, useMemo, useRef, useState } from 'react';

import type { ParsedSection } from '../utils/markdown-utils';
import { parseSectionAttributes, slugify } from '../utils/markdown-utils';

const PARSE_BATCH_SIZE = 10;
const PARSE_DEBOUNCE_MS = 400;

interface StreamingSection {
  id: string;
  openingTag: string;
  title: string;
  text: string;
  format: 'xml';
  attributes?: Record<string, string>;
  isComplete: boolean; // Whether we've seen the closing </section> tag
}

/**
 * Hook for parsing streaming XML sections from SSE deltas
 * Accumulates deltas and parses sections incrementally as they arrive
 */
export function useStreamingSections() {
  const [accumulatedContent, setAccumulatedContent] = useState<string>('');
  const [sections, setSections] = useState<StreamingSection[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // Track current section being built
  const currentSectionRef = useRef<{
    id: string | null;
    title: string | null;
    openingTag: string;
    content: string;
    attributes: Record<string, string>;
    startIndex: number;
  } | null>(null);
  const pendingDeltaCountRef = useRef(0);
  const latestContentRef = useRef('');
  const parseTimeoutRef = useRef<number | null>(null);

  // Reset state
  const clearScheduledParse = useCallback(() => {
    if (parseTimeoutRef.current) {
      window.clearTimeout(parseTimeoutRef.current);
      parseTimeoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearScheduledParse();
    setAccumulatedContent('');
    setSections([]);
    setIsStreaming(false);
    setIsComplete(false);
    currentSectionRef.current = null;
    pendingDeltaCountRef.current = 0;
    latestContentRef.current = '';
  }, [clearScheduledParse]);

  const parseStreamContent = useCallback((content: string) => {
    if (!content) {
      return;
    }

    // Extract content between <note> and </note> tags (or up to current position)
    const noteStartIndex = content.indexOf('<note>');
    const noteEndIndex = content.indexOf('</note>');
    const noteContent =
      noteStartIndex !== -1
        ? content.substring(
            noteStartIndex + '<note>'.length,
            noteEndIndex !== -1 ? noteEndIndex : content.length,
          )
        : content;

    // Parse sections from note content
    const sectionOpenRegex = /<section\b([^>]*)>/gi;
    const sectionsToUpdate: StreamingSection[] = [];
    let lastProcessedIndex = 0;
    let match: RegExpExecArray | null;

    // Reset regex
    sectionOpenRegex.lastIndex = 0;

    while ((match = sectionOpenRegex.exec(noteContent)) !== null) {
      const openIndex = match.index;
      const attributeString = match[1] || '';
      const openTagEnd = openIndex + match[0].length;

      // Check if this is a new section or continuation of current
      if (!currentSectionRef.current || openIndex > lastProcessedIndex) {
        // Start new section
        const attributes = parseSectionAttributes(attributeString);
        const rawTitle = attributes.title?.trim();
        const title = rawTitle || 'Untitled Section';
        const rawId = attributes.id?.trim();
        const generatedId = slugify(title || `section-${sectionsToUpdate.length + 1}`);
        const id = rawId || generatedId || `section-${sectionsToUpdate.length + 1}`;

        currentSectionRef.current = {
          id,
          title,
          openingTag: match[0],
          content: '',
          attributes,
          startIndex: openIndex,
        };
      }

      // Look for closing tag after this opening tag
      const afterOpenTag = noteContent.substring(openTagEnd);
      const closeTagIndex = afterOpenTag.indexOf('</section>');

      if (closeTagIndex !== -1) {
        // Found closing tag - extract content
        const contentWithinSection = afterOpenTag.substring(0, closeTagIndex);

        if (
          currentSectionRef.current &&
          currentSectionRef.current.id &&
          currentSectionRef.current.title
        ) {
          sectionsToUpdate.push({
            id: currentSectionRef.current.id,
            openingTag: currentSectionRef.current.openingTag,
            title: currentSectionRef.current.title,
            text: contentWithinSection.trim(),
            format: 'xml',
            attributes: currentSectionRef.current.attributes,
            isComplete: true,
          });

          lastProcessedIndex = openTagEnd + closeTagIndex + '</section>'.length;
          currentSectionRef.current = null;
        }
      } else {
        // No closing tag yet - accumulate content
        const partialContent = afterOpenTag;

        if (
          currentSectionRef.current &&
          currentSectionRef.current.id &&
          currentSectionRef.current.title
        ) {
          currentSectionRef.current.content = partialContent;
          sectionsToUpdate.push({
            id: currentSectionRef.current.id,
            openingTag: currentSectionRef.current.openingTag,
            title: currentSectionRef.current.title,
            text: partialContent.trim(),
            format: 'xml',
            attributes: currentSectionRef.current.attributes,
            isComplete: false,
          });
        }

        // Stop here - we're still building this section
        break;
      }
    }

    if (sectionsToUpdate.length > 0) {
      setSections(prevSections => {
        const sectionMap = new Map<string, StreamingSection>();

        // Add all previous sections
        prevSections.forEach(section => {
          sectionMap.set(section.id, section);
        });

        // Update/add new sections
        sectionsToUpdate.forEach(section => {
          const existing = sectionMap.get(section.id);
          if (existing && !existing.isComplete && section.isComplete) {
            // Replace incomplete with complete
            sectionMap.set(section.id, section);
          } else if (!existing) {
            // Add new section
            sectionMap.set(section.id, section);
          } else if (existing && !existing.isComplete && !section.isComplete) {
            // Update incomplete section content (use longer content)
            sectionMap.set(section.id, {
              ...section,
              text: section.text.length > existing.text.length ? section.text : existing.text,
            });
          }
        });

        return Array.from(sectionMap.values());
      });
    }
  }, []);

  const scheduleParse = useCallback(
    (content: string, { immediate = false }: { immediate?: boolean } = {}) => {
      if (immediate) {
        clearScheduledParse();
        pendingDeltaCountRef.current = 0;
        parseStreamContent(content);
        return;
      }

      if (parseTimeoutRef.current !== null) {
        return;
      }

      parseTimeoutRef.current = window.setTimeout(() => {
        parseTimeoutRef.current = null;
        pendingDeltaCountRef.current = 0;
        parseStreamContent(latestContentRef.current);
      }, PARSE_DEBOUNCE_MS);
    },
    [clearScheduledParse, parseStreamContent],
  );

  // Process a delta chunk - just accumulate content, parsing happens in useEffect
  const processDelta = useCallback(
    (delta: string) => {
      if (!delta) return;

      setIsStreaming(true);
      setAccumulatedContent(prev => {
        const nextContent = prev + delta;
        latestContentRef.current = nextContent;

        const containsSectionBoundary =
          delta.includes('</section>') || delta.includes('<section') || delta.includes('</note>');

        if (containsSectionBoundary) {
          scheduleParse(nextContent, { immediate: true });
        } else {
          pendingDeltaCountRef.current += 1;
          if (pendingDeltaCountRef.current >= PARSE_BATCH_SIZE) {
            scheduleParse(nextContent, { immediate: true });
          } else {
            scheduleParse(nextContent);
          }
        }

        return nextContent;
      });
    },
    [scheduleParse],
  );

  // Mark streaming as complete
  const markComplete = useCallback(() => {
    scheduleParse(latestContentRef.current, { immediate: true });
    setIsStreaming(false);
    setIsComplete(true);

    // Finalize any incomplete section
    if (currentSectionRef.current) {
      const currentSection = currentSectionRef.current;
      const sectionId = currentSection.id;
      const sectionTitle = currentSection.title;
      if (sectionId && sectionTitle) {
        setSections(prevSections => {
          const existingIndex = prevSections.findIndex(s => s.id === sectionId);
          if (existingIndex !== -1) {
            // Update existing incomplete section
            const updated = [...prevSections];
            updated[existingIndex] = {
              ...updated[existingIndex],
              text: currentSection.content.trim(),
              isComplete: true,
            };
            return updated;
          } else {
            // Add as complete section
            return [
              ...prevSections,
              {
                id: sectionId,
                openingTag: currentSection.openingTag,
                title: sectionTitle,
                text: currentSection.content.trim(),
                format: 'xml',
                attributes: currentSection.attributes,
                isComplete: true,
              },
            ];
          }
        });
      }
      currentSectionRef.current = null;
    }
  }, [scheduleParse]);

  // Convert streaming sections to ParsedSection format
  const parsedSections: ParsedSection[] = useMemo(() => {
    return sections.map((section, index) => {
      // Keep source positions tied to the original XML spelling. Decoded
      // attributes cannot reconstruct numeric entities or quoting exactly.
      const sectionStartTag = section.openingTag;

      // Find section in accumulated content (within <note> tags)
      const noteStartIndex = accumulatedContent.indexOf('<note>');
      const noteContent =
        noteStartIndex !== -1
          ? accumulatedContent.substring(noteStartIndex + '<note>'.length)
          : accumulatedContent;

      // Search for this section's opening tag
      let searchStart = 0;
      for (let i = 0; i < index; i++) {
        // Find previous section's end
        const prevSection = sections[i];
        const prevStartTag = prevSection.openingTag;
        const prevStartInNote = noteContent.indexOf(prevStartTag, searchStart);
        if (prevStartInNote !== -1) {
          const prevEndInNote = noteContent.indexOf('</section>', prevStartInNote);
          if (prevEndInNote !== -1) {
            searchStart = prevEndInNote + '</section>'.length;
          }
        }
      }

      const sectionStartInNote = noteContent.indexOf(sectionStartTag, searchStart);
      const sectionStartIndex =
        sectionStartInNote !== -1 && noteStartIndex !== -1
          ? noteStartIndex + '<note>'.length + sectionStartInNote
          : index * 1000;
      const contentStartIndex = sectionStartIndex + sectionStartTag.length;
      const contentEndIndex = contentStartIndex + section.text.length;
      const endIndex = contentEndIndex + '</section>'.length;

      return {
        id: section.id,
        title: section.title,
        text: section.text,
        startIndex: sectionStartIndex,
        endIndex: endIndex,
        contentStartIndex: contentStartIndex,
        contentEndIndex: contentEndIndex,
        format: section.format,
        attributes: section.attributes,
      };
    });
  }, [sections, accumulatedContent]);

  return {
    sections: parsedSections,
    accumulatedContent,
    isStreaming,
    isComplete,
    processDelta,
    markComplete,
    reset,
  };
}
